import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { cn } from '@sandtech-hub-monorepo/ui-kit';
import { ViewerDocument } from '@sandtech-hub-monorepo/ehub-client';

import { PageHero } from '../../components/PageHero';
import { EnrolledProgramList } from '../../components/program/EnrolledProgramList';
import welcomeHero from '../../assets/gsa/welcome-hero.webp';
import { GsaEnrollment } from '../../data/gsaEnrollments';
import { useSubscriptionState } from '../../data/useSubscriptionState';
import { AppRoutesPaths } from '../../routes/types';
import { SubscriptionBanner } from './components/SubscriptionBanner';

interface Props {
  enrollments: GsaEnrollment[];
}

export const GsaDashboard = ({ enrollments }: Props) => {
  const { data } = useQuery(ViewerDocument, { fetchPolicy: 'cache-first' });
  const { state, daysRemaining, loading: subscriptionLoading } =
    useSubscriptionState();

  const [recommendedOpen, setRecommendedOpen] = useState(false);

  const firstName = data?.viewer?.firstName;

  return (
    <div className="bg-background pb-16">
      <PageHero backgroundImage={welcomeHero}>
        <p className="text-sm font-semibold uppercase tracking-wide text-accent">
          Why Join our Community
        </p>
        <h1 className="mt-2 max-w-3xl text-3xl font-semibold md:text-4xl">
          We empower students with top-tier education and an inclusive
          community.
        </h1>
        <p className="mt-3 text-white/90">
          Track your progress, achieve your goals.
        </p>
      </PageHero>

      <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-6 px-4 sm:px-8 md:px-12 py-8">
        <div>
          <h2 className="text-2xl font-semibold text-heading">
            Hi {firstName || 'there'}!
          </h2>
          <p className="text-muted-foreground">
            Every step you take brings you closer to success.
          </p>
        </div>

        <SubscriptionBanner
          state={state}
          daysRemaining={daysRemaining}
          programId={enrollments[0]?.programId}
          loading={subscriptionLoading}
        />

        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-heading">My programs</h3>
          <Link
            to={AppRoutesPaths.Learning}
            className="flex items-center gap-1 text-sm font-medium text-primary"
          >
            View More
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <EnrolledProgramList enrollments={enrollments} />

        <div className="rounded-2xl border border-border-secondary bg-card">
          <button
            type="button"
            onClick={() => setRecommendedOpen((o) => !o)}
            className="flex w-full items-center justify-between p-6"
          >
            <span className="text-lg font-semibold text-heading">
              Recommended Programs
            </span>
            <ChevronDown
              className={cn(
                'h-5 w-5 text-muted-foreground transition-transform',
                recommendedOpen && 'rotate-180'
              )}
            />
          </button>
          {recommendedOpen && (
            <p className="px-6 pb-6 text-sm text-muted-foreground">
              Personalized recommendations will appear here as you learn.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
